/**
 * treatabilityAnalysis.ts — Scientific Water Treatability Engine
 *
 * Provides physics/chemistry/biology-based analysis functions for:
 * - Industry origin identification from parameter signatures
 * - BOD/COD biodegradability assessment
 * - Nutrient balance (BOD:N:P)
 * - Toxicity & inhibitor screening (Metcalf & Eddy thresholds)
 * - Alkalinity / buffering capacity
 * - Equipment suitability scoring matrix
 * - Recommended treatment train generation
 *
 * All thresholds sourced from:
 *   Metcalf & Eddy (Wastewater Engineering, 5th Ed.)
 *   WEF Manual of Practice
 *   UK Water Industry Research (UKWIR) guidelines
 *   EPA Process Design Manual
 */

import type { WaterParameters } from '../types/calculator';

// ─── Exported Types ────────────────────────────────────────────────────────

export interface IndustryOrigin {
  primary: string;
  secondary?: string;
  confidence: 'high' | 'medium' | 'low';
  confidencePct: number;
  indicators: string[];
  characteristics: string;
}

export interface BiodegradabilityAssessment {
  bodCodRatio: number;
  classification: 'highly_biodegradable' | 'readily_biodegradable' | 'moderately_biodegradable' | 'poorly_biodegradable' | 'recalcitrant';
  label: string;
  color: string;
  biologicalTreatmentSuitability: number; // 0–100
  expectedBODRemoval: number; // % in conventional AS
  description: string;
  recommendation: string;
}

export interface NutrientBalanceAssessment {
  bodNRatio: number | null;
  bodPRatio: number | null;
  idealBodNRatio: 20;
  idealBodPRatio: 100;
  nStatus: 'deficient' | 'adequate' | 'excess';
  pStatus: 'deficient' | 'adequate' | 'excess';
  notes: string[];
  supplementRequired: boolean;
  removeNutrients: boolean;
  supplementChemicals: string[];
}

export interface ToxicityItem {
  parameter: string;
  label: string;
  value: number;
  unit: string;
  bioInhibitionThreshold: number;
  acuteThreshold: number;
  riskLevel: 'none' | 'low' | 'moderate' | 'high' | 'critical';
  riskLabel: string;
  percentOfThreshold: number;
  biologicalEffect: string;
  mitigation: string;
}

export interface BufferingAssessment {
  alkalinity: number;
  ph: number;
  adequateBuffering: boolean;
  recommendedMinAlkalinity: number; // mg/L as CaCO3
  alka_mgL_perBOD: number; // alkalinity consumed per mg/L BOD nitrified
  nitrificationRisk: 'low' | 'moderate' | 'high';
  supplementRequired: boolean;
  supplement: string;
  supplementDose: string;
  notes: string[];
}

export interface EquipmentSuitability {
  id: string;
  name: string;
  path: string;
  score: number; // 0–100
  priority: 'essential' | 'recommended' | 'optional' | 'not_applicable';
  stage: 'pre-treatment' | 'primary' | 'secondary' | 'tertiary' | 'polishing';
  triggerParams: { param: string; value: number; threshold: number; unit: string }[];
  reason: string;
  designNote: string;
}

export interface TreatmentTrainStep {
  order: number;
  equipment: string;
  equipmentId: string;
  stage: 'pre-treatment' | 'primary' | 'secondary' | 'tertiary' | 'polishing';
  purpose: string;
  keyRemoval: { param: string; label: string; expected: number }[];
  chemDosing?: { chemical: string; dose: string; purpose: string }[];
  designParam: string;
}

export interface SettleabilityAssessment {
  classification: 'non-settleable' | 'slowly-settleable' | 'readily-settleable' | 'highly-settleable';
  score: number;
  tssCategory: string;
  turbidityCategory: string;
  primaryTreatmentEffective: boolean;
  dafSuitable: boolean;
  lamellasuitable: boolean;
  notes: string[];
}

export interface TreatabilityReport {
  sampleName: string;
  sampleDate: string;
  industryOrigin: IndustryOrigin;
  biodegradability: BiodegradabilityAssessment;
  nutrientBalance: NutrientBalanceAssessment;
  toxicityScreen: ToxicityItem[];
  buffering: BufferingAssessment;
  settleability: SettleabilityAssessment;
  overallTreatabilityScore: number; // 0–100
  treatabilityRating: 'excellent' | 'good' | 'moderate' | 'challenging' | 'complex';
  treatabilityColor: string;
  equipmentSuitability: EquipmentSuitability[];
  recommendedTrain: TreatmentTrainStep[];
  keyFindings: string[];
  warnings: string[];
}

// ─── Effluent Compliance Types ─────────────────────────────────────────────

export interface ComplianceParameter {
  param: string;
  label: string;
  unit: string;
  value: number;
  limit: number;
  ratio: number; // value/limit
  pass: boolean;
  margin: number; // % under (positive) or over (negative) limit
  riskLevel: 'compliant' | 'near_limit' | 'exceeded' | 'critical';
  color: string;
}

export interface RemovalEfficiency {
  param: string;
  label: string;
  unit: string;
  influentValue: number;
  effluentValue: number;
  removalPct: number;
  expectedMin: number;
  expectedMax: number;
  performance: 'excellent' | 'good' | 'adequate' | 'poor' | 'failing';
}

export interface RegulatoryRisk {
  level: 'low' | 'moderate' | 'high' | 'critical';
  score: number; // 0–100
  exceedingParams: string[];
  nearLimitParams: string[];
  consequenceNotes: string[];
  immediateActions: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────

/** Biological inhibition thresholds (mg/L) — Metcalf & Eddy Table 7-15 */
const BIO_INHIBITION: Record<string, { inhibition: number; acute: number; effect: string; mitigation: string }> = {
  lead:      { inhibition: 0.10, acute: 0.50, effect: 'Enzyme disruption, biomass toxicity',           mitigation: 'Chemical precipitation (lime/NaOH), pH>9.5' },
  mercury:   { inhibition: 0.01, acute: 0.05, effect: 'Severe enzyme inhibition, sludge toxicity',     mitigation: 'Sulphide precipitation, activated carbon polishing' },
  cadmium:   { inhibition: 0.02, acute: 0.10, effect: 'Inhibits nitrification, cell membrane damage',  mitigation: 'Precipitation at pH 8.5–11, coagulation' },
  chromium:  { inhibition: 0.50, acute: 2.00, effect: 'Cr(VI) highly toxic to biomass; Cr(III) moderate', mitigation: 'Reduction to Cr(III) then precipitation at pH 8–9' },
  nickel:    { inhibition: 1.00, acute: 5.00, effect: 'Nitrification inhibition at >1 mg/L',           mitigation: 'Precipitation at pH 8.5–10, ion exchange' },
  zinc:      { inhibition: 1.00, acute: 5.00, effect: 'Biomass inhibition, sludge accumulation',       mitigation: 'Precipitation at pH 8–10, coagulation' },
  copper:    { inhibition: 0.50, acute: 2.00, effect: 'Nitrification inhibitor at low concentrations', mitigation: 'Precipitation at pH 7.5–9, sulphide addition' },
  phenol:    { inhibition: 20.0, acute: 100.0, effect: 'Disrupts cell membranes, inhibits biological oxidation', mitigation: 'Extended aeration, adapted seed sludge, dilution' },
  sulfides:  { inhibition: 5.00, acute: 20.0, effect: 'H₂S generation, corrosion, odour, inhibits nitrification', mitigation: 'pH elevation, iron dosing for FeS precipitation, aeration' },
  surfactants: { inhibition: 20.0, acute: 50.0, effect: 'Foaming, reduced oxygen transfer, membrane fouling', mitigation: 'DAF, mechanical foam breakers, adapted culture' },
};

/** Expected removal efficiencies (%) by equipment — WEF MOP */
const EQUIPMENT_REMOVALS: Record<string, Record<string, [number, number]>> = {
  'daf':       { tss: [70, 95], oilGrease: [85, 98], bod5: [20, 50], cod: [15, 40], totalPhosphorus: [30, 60] },
  'lamella':   { tss: [60, 90], bod5: [15, 35], cod: [10, 25] },
  'aeration':  { bod5: [85, 98], cod: [75, 95], ammoniaN: [85, 99], totalNitrogen: [30, 85], tss: [80, 95] },
  'secondary': { tss: [80, 95], bod5: [10, 30], cod: [5, 20] },
  'gac':       { cod: [40, 80], bod5: [30, 70], color: [60, 95], tds: [0, 20] },
  'multimedia':{ tss: [50, 90], turbidity: [60, 95], bod5: [5, 20] },
  'ph':        { lead: [70, 99], chromium: [80, 99], cadmium: [70, 95], zinc: [70, 95], nickel: [50, 90], copper: [70, 95] },
  'eq':        { bod5: [0, 10], tss: [0, 15] },
  'ammonia-stripper': { ammoniaN: [90, 99], totalNitrogen: [40, 80] },
};

// ─── Industry Origin ───────────────────────────────────────────────────────

function scoreIndustries(p: WaterParameters): Record<string, number> {
  const scores: Record<string, number> = {};
  const ratio = p.cod > 0 ? p.bod5 / p.cod : 0;
  const hasMetals = [p.lead, p.chromium, p.cadmium, p.nickel, p.zinc, p.copper].some(v => v > 0.1);

  // Food Processing — high BOD, high O&G, good biodegradability
  let s = 0;
  if (p.bod5 > 150)    s += 20; if (p.bod5 > 500)   s += 15;
  if (p.oilGrease > 25) s += 25; if (p.oilGrease > 80) s += 15;
  if (ratio > 0.5)     s += 20; if (p.ph >= 5 && p.ph <= 8.5) s += 5;
  scores['Food Processing'] = Math.min(s, 100);

  // Dairy — very high BOD, very high O&G, high TP, alkaline
  s = 0;
  if (p.bod5 > 500)    s += 30; if (p.bod5 > 1200) s += 20;
  if (p.oilGrease > 60) s += 25; if (p.totalPhosphorus > 5) s += 15;
  if (ratio > 0.6)     s += 10;
  scores['Dairy Processing'] = Math.min(s, 100);

  // Brewery / Beverage — high BOD, low O&G, acidic pH
  s = 0;
  if (p.bod5 > 300 && p.bod5 < 3000)  s += 25;
  if (ratio > 0.55)    s += 30;
  if (p.ph >= 3.5 && p.ph <= 6.5)     s += 20;
  if (p.oilGrease < 30 && p.bod5 > 300) s += 15;
  if (p.tss < 600 && p.bod5 > 300)    s += 10;
  scores['Brewery/Beverage'] = Math.min(s, 100);

  // Municipal / Commercial — moderate BOD, balanced nutrients
  s = 0;
  if (p.bod5 >= 80 && p.bod5 <= 400)  s += 25;
  if (p.tss >= 80 && p.tss <= 350)    s += 20;
  if (p.ammoniaN >= 15 && p.ammoniaN <= 80) s += 30;
  if (p.totalPhosphorus >= 2 && p.totalPhosphorus <= 15) s += 15;
  if (p.surfactants > 3)               s += 10;
  scores['Municipal/Commercial'] = Math.min(s, 100);

  // Chemical / Pharmaceutical — low BOD:COD, phenol, sulfides
  s = 0;
  if (ratio < 0.3 && p.cod > 200)     s += 35;
  if (p.phenol > 5)                    s += 30;
  if (p.sulfides > 3)                  s += 25;
  if (p.tds > 4000)                    s += 10;
  scores['Chemical/Pharmaceutical'] = Math.min(s, 100);

  // Mining / Metal Processing — metals, low BOD, high TDS
  s = 0;
  if (hasMetals)                        s += 45;
  if (p.tds > 3000)                    s += 20;
  if (p.conductivity > 3000)           s += 15;
  if (p.bod5 < 80)                     s += 20;
  scores['Mining/Metal Processing'] = Math.min(s, 100);

  // Agricultural / Livestock — very high ammonia, high TP, high BOD
  s = 0;
  if (p.ammoniaN > 60)                 s += 40;
  if (p.totalPhosphorus > 15)         s += 30;
  if (p.bod5 > 300)                    s += 20;
  if (p.tss > 200)                     s += 10;
  scores['Agricultural/Livestock'] = Math.min(s, 100);

  // Petroleum / Refinery — very high O&G, phenol, sulfides
  s = 0;
  if (p.oilGrease > 100)              s += 40;
  if (p.phenol > 5)                    s += 25;
  if (p.sulfides > 10)                 s += 25;
  if (p.tds > 5000)                    s += 10;
  scores['Petroleum/Refinery'] = Math.min(s, 100);

  // Textile — high colour, surfactants, variable pH
  s = 0;
  if (p.color > 200)                   s += 35;
  if (p.surfactants > 10)              s += 30;
  if (p.tds > 2000)                    s += 20;
  if (ratio < 0.4 && p.cod > 150)     s += 15;
  scores['Textile/Dyeing'] = Math.min(s, 100);

  return scores;
}

export function inferIndustryOrigin(p: WaterParameters): IndustryOrigin {
  const scores = scoreIndustries(p);
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [primaryName, primaryScore] = sorted[0];
  const [secondaryName, secondaryScore] = sorted[1] ?? ['', 0];

  const confidence: 'high' | 'medium' | 'low' =
    primaryScore >= 70 ? 'high' :
    primaryScore >= 45 ? 'medium' : 'low';

  // Build indicator list
  const ratio = p.cod > 0 ? p.bod5 / p.cod : 0;
  const indicators: string[] = [];
  if (p.bod5 > 500)           indicators.push(`High BOD₅ ${p.bod5} mg/L`);
  if (p.oilGrease > 30)       indicators.push(`Elevated O&G ${p.oilGrease} mg/L`);
  if (p.ammoniaN > 50)        indicators.push(`High NH₃-N ${p.ammoniaN} mg/L`);
  if (p.totalPhosphorus > 5)  indicators.push(`Elevated TP ${p.totalPhosphorus} mg/L`);
  if (p.phenol > 5)           indicators.push(`Phenol detected ${p.phenol} mg/L`);
  if (p.sulfides > 2)         indicators.push(`Sulfides present ${p.sulfides} mg/L`);
  if (p.color > 150)          indicators.push(`High colour ${p.color} Pt-Co`);
  if (p.tds > 3000)           indicators.push(`High TDS ${p.tds} mg/L`);
  if ([p.lead, p.chromium, p.cadmium, p.nickel, p.zinc, p.copper].some(v => v > 0.1))
    indicators.push('Heavy metals detected');
  indicators.push(`BOD:COD ratio ${ratio.toFixed(2)}`);

  const characteristics: Record<string, string> = {
    'Food Processing':       'High organic load, readily biodegradable, fats and proteins dominant',
    'Dairy Processing':      'Very high BOD/COD, emulsified fats, lactose, proteins, high TP',
    'Brewery/Beverage':      'High soluble BOD, sugars and alcohols, slightly acidic, good biodegradability',
    'Municipal/Commercial':  'Balanced nutrients, moderate strength, readily biodegradable domestic waste',
    'Chemical/Pharmaceutical': 'Complex organics, low biodegradability, potential inhibitors',
    'Mining/Metal Processing': 'Heavy metals dominant, low organic content, high dissolved solids',
    'Agricultural/Livestock': 'Very high ammonia and phosphorus, seasonal variation, animal waste',
    'Petroleum/Refinery':    'Free and emulsified oil dominant, phenolic compounds, H₂S',
    'Textile/Dyeing':        'High colour, surfactants, complex dyes, variable pH and TDS',
  };

  return {
    primary: primaryName,
    secondary: secondaryScore > 30 ? secondaryName : undefined,
    confidence,
    confidencePct: primaryScore,
    indicators,
    characteristics: characteristics[primaryName] ?? 'Mixed/unclassified industrial wastewater',
  };
}

// ─── Biodegradability ──────────────────────────────────────────────────────

export function assessBiodegradability(p: WaterParameters): BiodegradabilityAssessment {
  const ratio = p.cod > 0 ? p.bod5 / p.cod : 0;

  if (ratio >= 0.6) return {
    bodCodRatio: ratio, classification: 'highly_biodegradable',
    label: 'Highly Biodegradable', color: '#10b981',
    biologicalTreatmentSuitability: 95, expectedBODRemoval: 95,
    description: 'Excellent substrate for biological treatment. Simple sugars, proteins, and readily oxidisable organic matter dominate.',
    recommendation: 'Conventional activated sludge or MBBR is ideal. Minimal pre-treatment required beyond primary settling.',
  };
  if (ratio >= 0.4) return {
    bodCodRatio: ratio, classification: 'readily_biodegradable',
    label: 'Readily Biodegradable', color: '#84cc16',
    biologicalTreatmentSuitability: 80, expectedBODRemoval: 90,
    description: 'Good biodegradability. Mixed organic compounds with some slowly degradable fraction.',
    recommendation: 'Standard biological treatment. Consider extended SRT for complete COD removal.',
  };
  if (ratio >= 0.2) return {
    bodCodRatio: ratio, classification: 'moderately_biodegradable',
    label: 'Moderately Biodegradable', color: '#f59e0b',
    biologicalTreatmentSuitability: 55, expectedBODRemoval: 75,
    description: 'Significant non-biodegradable COD fraction. Complex organics or inhibitors may be present.',
    recommendation: 'Biological treatment must be supplemented with physico-chemical processes (coagulation, GAC). Consider bioaugmentation.',
  };
  if (ratio >= 0.1) return {
    bodCodRatio: ratio, classification: 'poorly_biodegradable',
    label: 'Poorly Biodegradable', color: '#f97316',
    biologicalTreatmentSuitability: 25, expectedBODRemoval: 50,
    description: 'Low biological treatability. High proportion of refractory organics, possible inhibitors or xenobiotics.',
    recommendation: 'Advanced oxidation (Fenton, ozone/H₂O₂), physico-chemical treatment primary. Biological polishing only.',
  };
  return {
    bodCodRatio: ratio, classification: 'recalcitrant',
    label: 'Recalcitrant / Non-biodegradable', color: '#ef4444',
    biologicalTreatmentSuitability: 5, expectedBODRemoval: 20,
    description: 'Extremely poor biodegradability. Xenobiotics, polymers, or heavily inhibited matrix.',
    recommendation: 'Physical/chemical treatment essential: adsorption (GAC/PAC), advanced oxidation, solvent extraction, incineration.',
  };
}

// ─── Nutrient Balance ──────────────────────────────────────────────────────

export function assessNutrientBalance(p: WaterParameters): NutrientBalanceAssessment {
  const n = p.ammoniaN > 0 ? p.ammoniaN : p.tkn > 0 ? p.tkn : p.totalNitrogen;
  const pval = p.totalPhosphorus;

  const bodNRatio = n > 0 ? p.bod5 / n : null;
  const bodPRatio = pval > 0 ? p.bod5 / pval : null;

  // Ideal: BOD:N = 20:1, BOD:P = 100:1 (Metcalf & Eddy)
  const nStatus: 'deficient' | 'adequate' | 'excess' =
    bodNRatio === null ? 'adequate' :
    bodNRatio > 25   ? 'deficient' :
    bodNRatio < 10   ? 'excess' : 'adequate';

  const pStatus: 'deficient' | 'adequate' | 'excess' =
    bodPRatio === null ? 'adequate' :
    bodPRatio > 120  ? 'deficient' :
    bodPRatio < 60   ? 'excess' : 'adequate';

  const notes: string[] = [];
  const supplements: string[] = [];

  if (nStatus === 'deficient') {
    notes.push(`N-deficient: BOD:N = ${bodNRatio?.toFixed(1)} (ideal ≤20). Biological treatment requires N addition.`);
    supplements.push('Ammonium sulphate or urea supplementation recommended');
  }
  if (nStatus === 'excess') {
    notes.push(`Excess nitrogen: BOD:N = ${bodNRatio?.toFixed(1)}. Nitrification-denitrification required to meet discharge limits.`);
  }
  if (pStatus === 'deficient') {
    notes.push(`P-deficient: BOD:P = ${bodPRatio?.toFixed(1)} (ideal ≤100). Phosphorus dosing required for biological activity.`);
    supplements.push('Phosphoric acid or superphosphate dosing required');
  }
  if (pStatus === 'excess') {
    notes.push(`Excess phosphorus: BOD:P = ${bodPRatio?.toFixed(1)}. Chemical P removal (FeCl₃/alum) required for discharge compliance.`);
  }

  return {
    bodNRatio, bodPRatio,
    idealBodNRatio: 20, idealBodPRatio: 100,
    nStatus, pStatus, notes,
    supplementRequired: nStatus === 'deficient' || pStatus === 'deficient',
    removeNutrients: nStatus === 'excess' || pStatus === 'excess',
    supplementChemicals: supplements,
  };
}

// ─── Toxicity Screening ────────────────────────────────────────────────────

export function screenToxicity(p: WaterParameters): ToxicityItem[] {
  const checks: { param: keyof WaterParameters; label: string; unit: string; key: string }[] = [
    { param: 'lead',        label: 'Lead (Pb)',       unit: 'mg/L', key: 'lead' },
    { param: 'mercury',     label: 'Mercury (Hg)',    unit: 'mg/L', key: 'mercury' },
    { param: 'cadmium',     label: 'Cadmium (Cd)',    unit: 'mg/L', key: 'cadmium' },
    { param: 'chromium',    label: 'Chromium (Cr)',   unit: 'mg/L', key: 'chromium' },
    { param: 'nickel',      label: 'Nickel (Ni)',     unit: 'mg/L', key: 'nickel' },
    { param: 'zinc',        label: 'Zinc (Zn)',       unit: 'mg/L', key: 'zinc' },
    { param: 'copper',      label: 'Copper (Cu)',     unit: 'mg/L', key: 'copper' },
    { param: 'phenol',      label: 'Phenol',          unit: 'mg/L', key: 'phenol' },
    { param: 'sulfides',    label: 'Sulfides (H₂S)',  unit: 'mg/L', key: 'sulfides' },
    { param: 'surfactants', label: 'Surfactants',     unit: 'mg/L', key: 'surfactants' },
  ];

  return checks
    .map(({ param, label, unit, key }) => {
      const value = (p as Record<string, number>)[param as string] ?? 0;
      const ref = BIO_INHIBITION[key];
      if (!ref || value <= 0) return null;

      const pct = (value / ref.inhibition) * 100;
      const riskLevel: ToxicityItem['riskLevel'] =
        value >= ref.acute      ? 'critical' :
        value >= ref.inhibition ? 'high' :
        pct >= 75               ? 'moderate' :
        pct >= 40               ? 'low' : 'none';

      const riskLabel: Record<ToxicityItem['riskLevel'], string> = {
        none: 'No Risk', low: 'Low Risk', moderate: 'Moderate',
        high: 'Inhibitory', critical: 'Critical',
      };

      return {
        parameter: param as string, label, value, unit,
        bioInhibitionThreshold: ref.inhibition,
        acuteThreshold: ref.acute,
        riskLevel, riskLabel: riskLabel[riskLevel],
        percentOfThreshold: Math.round(pct),
        biologicalEffect: ref.effect,
        mitigation: ref.mitigation,
      } satisfies ToxicityItem;
    })
    .filter((x): x is ToxicityItem => x !== null && x.riskLevel !== 'none');
}

// ─── Alkalinity / Buffering ────────────────────────────────────────────────

export function assessBuffering(p: WaterParameters): BufferingAssessment {
  // Nitrification consumes ~7.1 mg alkalinity per mg NH₃-N oxidised
  const alkaConsumed = p.ammoniaN * 7.14;
  const residualAlka = p.alkalinity - alkaConsumed;
  const recommended = Math.max(80, alkaConsumed + 100); // keep 100 mg/L CaCO3 residual

  const adequate = p.alkalinity >= recommended;
  const nitrificationRisk: BufferingAssessment['nitrificationRisk'] =
    residualAlka < 0   ? 'high' :
    residualAlka < 50  ? 'moderate' : 'low';

  const notes: string[] = [];
  if (!adequate) {
    notes.push(`Alkalinity ${p.alkalinity} mg/L insufficient for nitrification (requires ~${Math.round(recommended)} mg/L)`);
    notes.push(`${Math.round(alkaConsumed)} mg/L alkalinity will be consumed oxidising NH₃-N`);
  }
  if (p.ph < 6.5) notes.push('Low pH may inhibit biological activity — alkalinity addition will also raise pH');
  if (p.ph > 8.5) notes.push('High pH may indicate excess alkalinity or high carbonate content');

  return {
    alkalinity: p.alkalinity, ph: p.ph,
    adequateBuffering: adequate,
    recommendedMinAlkalinity: Math.round(recommended),
    alka_mgL_perBOD: 7.14,
    nitrificationRisk,
    supplementRequired: !adequate,
    supplement: 'Sodium bicarbonate (NaHCO₃) or lime (Ca(OH)₂)',
    supplementDose: !adequate ? `${Math.round((recommended - p.alkalinity) * 1.2)} mg/L NaHCO₃ equivalent` : 'Not required',
    notes,
  };
}

// ─── Settleability ─────────────────────────────────────────────────────────

export function assessSettleability(p: WaterParameters): SettleabilityAssessment {
  const notes: string[] = [];
  let score = 0;

  const tssCategory =
    p.tss > 500  ? 'Very High (>500 mg/L)' :
    p.tss > 200  ? 'High (200–500 mg/L)'   :
    p.tss > 80   ? 'Moderate (80–200 mg/L)' :
    p.tss > 30   ? 'Low (30–80 mg/L)'       : 'Very Low (<30 mg/L)';

  const turbidityCategory =
    p.turbidity > 100  ? 'Very High (>100 NTU)' :
    p.turbidity > 50   ? 'High (50–100 NTU)'    :
    p.turbidity > 10   ? 'Moderate (10–50 NTU)' : 'Low (<10 NTU)';

  if (p.tss > 100) { score += 30; notes.push(`TSS ${p.tss} mg/L — gravity settling viable`); }
  if (p.turbidity > 20) { score += 20; notes.push(`Turbidity ${p.turbidity} NTU — colloidal/suspended solids present`); }
  if (p.oilGrease > 20) { score += 25; notes.push(`O&G ${p.oilGrease} mg/L — DAF recommended for flotation`); }
  if (p.tss > 200 || p.turbidity > 50) score += 15;

  const classification: SettleabilityAssessment['classification'] =
    score >= 70  ? 'highly-settleable'   :
    score >= 45  ? 'readily-settleable'  :
    score >= 20  ? 'slowly-settleable'   : 'non-settleable';

  return {
    classification, score, tssCategory, turbidityCategory,
    primaryTreatmentEffective: p.tss > 60,
    dafSuitable: p.oilGrease > 15 || p.tss > 80 || p.turbidity > 20,
    lamellasuitable: p.tss > 50 && p.oilGrease < 50,
    notes,
  };
}

// ─── Equipment Suitability Matrix ─────────────────────────────────────────

export function scoreEquipmentSuitability(p: WaterParameters): EquipmentSuitability[] {
  const ratio = p.cod > 0 ? p.bod5 / p.cod : 0;
  const eq: EquipmentSuitability[] = [];

  // EQ Tank
  eq.push({
    id: 'eq', name: 'Equalization Tank', path: '/eq-spec',
    score: 85, priority: 'essential', stage: 'pre-treatment',
    triggerParams: [{ param: 'Flow Variability', value: p.flowRate, threshold: 0, unit: 'm³/d' }],
    reason: 'Flow and load equalisation reduces downstream shock loading and ensures stable performance.',
    designNote: `Detention: 8–16 h. Working volume: ${(p.flowRate * 0.5).toFixed(0)} m³ (est. 12 h HRT).`,
  });

  // pH Adjustment
  const phScore = p.ph < 5.5 || p.ph > 10.5 ? 100 : p.ph < 6.0 || p.ph > 9.5 ? 85 : p.ph < 6.5 || p.ph > 9.0 ? 60 : 30;
  eq.push({
    id: 'ph', name: 'pH Adjustment', path: '/ph-spec',
    score: phScore,
    priority: phScore >= 80 ? 'essential' : phScore >= 55 ? 'recommended' : 'optional',
    stage: 'pre-treatment',
    triggerParams: [{ param: 'pH', value: p.ph, threshold: 6.5, unit: '' }],
    reason: p.ph < 6.5 ? `Acidic pH ${p.ph.toFixed(1)} will inhibit biological treatment and must be corrected.` :
            p.ph > 9.0 ? `Alkaline pH ${p.ph.toFixed(1)} requires acid dosing before secondary treatment.` :
            `pH ${p.ph.toFixed(1)} is within range but monitoring and adjustment may be needed.`,
    designNote: `Target: pH 7.0–8.0. ${p.ph < 7 ? 'Caustic soda (NaOH) or lime dosing.' : 'Sulphuric/hydrochloric acid dosing.'}`,
  });

  // DAF
  const dafScore =
    p.oilGrease > 100 || p.tss > 300 ? 100 :
    p.oilGrease > 50  || p.tss > 150 ? 90  :
    p.oilGrease > 20  || p.tss > 80  ? 75  :
    p.turbidity > 30                  ? 55  : 25;
  eq.push({
    id: 'daf', name: 'DAF Unit', path: '/daf-spec',
    score: dafScore,
    priority: dafScore >= 80 ? 'essential' : dafScore >= 55 ? 'recommended' : 'optional',
    stage: 'primary',
    triggerParams: [
      { param: 'O&G', value: p.oilGrease, threshold: 20, unit: 'mg/L' },
      { param: 'TSS', value: p.tss, threshold: 80, unit: 'mg/L' },
    ],
    reason: dafScore >= 80
      ? `O&G ${p.oilGrease} mg/L and TSS ${p.tss} mg/L — DAF is primary separation requirement.`
      : dafScore >= 55
      ? `Moderate O&G/TSS loading — DAF provides effective floatable solids removal.`
      : `Lower solids load — DAF optional; lamella or settling may suffice.`,
    designNote: `SLR: 2–8 m³/m²·h. Recycle rate: 15–25%. Saturator: 4–6 bar. Coagulant + polymer dosing required.`,
  });

  // Lamella Clarifier
  const lamellaScore =
    p.tss > 200 && p.oilGrease < 40 ? 90 :
    p.tss > 100 && p.oilGrease < 40 ? 75 :
    p.tss > 50                       ? 55 : 25;
  eq.push({
    id: 'lamella', name: 'Lamella Clarifier', path: '/lamella-spec',
    score: lamellaScore,
    priority: lamellaScore >= 75 ? 'recommended' : lamellaScore >= 50 ? 'optional' : 'not_applicable',
    stage: 'primary',
    triggerParams: [{ param: 'TSS', value: p.tss, threshold: 100, unit: 'mg/L' }],
    reason: `TSS ${p.tss} mg/L — ${lamellaScore >= 75 ? 'gravity settling effective with inclined plates.' : 'lower TSS reduces lamella benefit.'}`,
    designNote: 'SOR: 0.5–1.5 m³/m²·h. Plate angle: 55°. Coagulation pre-treatment required.',
  });

  // Aeration / MBBR — biological
  const bioScore =
    p.bod5 > 100 && ratio > 0.4 ? 100 :
    p.bod5 > 50  && ratio > 0.3 ? 80  :
    p.bod5 > 20  && ratio > 0.2 ? 60  :
    p.bod5 > 10                  ? 40  : 15;
  eq.push({
    id: 'aeration', name: 'Aeration / MBBR', path: '/aeration-spec',
    score: bioScore,
    priority: bioScore >= 80 ? 'essential' : bioScore >= 55 ? 'recommended' : 'optional',
    stage: 'secondary',
    triggerParams: [
      { param: 'BOD₅', value: p.bod5, threshold: 50, unit: 'mg/L' },
      { param: 'BOD:COD', value: ratio, threshold: 0.3, unit: '' },
    ],
    reason: bioScore >= 80
      ? `BOD₅ ${p.bod5} mg/L with BOD:COD ${ratio.toFixed(2)} — highly suitable for aerobic biological treatment.`
      : `BOD₅ ${p.bod5} mg/L — biological treatment viable but supplementary treatment may be needed.`,
    designNote: `F/M: 0.1–0.4 kg BOD/kg MLSS·d. SRT: 10–20 d. DO: 2–3 mg/L. HRT: 6–24 h.`,
  });

  // Secondary Clarifier
  eq.push({
    id: 'secondary', name: 'Secondary Clarifier', path: '/secondary-spec',
    score: bioScore > 60 ? 95 : 40,
    priority: bioScore > 60 ? 'essential' : 'optional',
    stage: 'secondary',
    triggerParams: [{ param: 'BOD₅', value: p.bod5, threshold: 50, unit: 'mg/L' }],
    reason: 'Biomass separation following biological treatment — required in all activated sludge systems.',
    designNote: 'SOR: 0.5–1.2 m³/m²·h. SLR: 3–6 kg/m²·h. SVI monitoring required.',
  });

  // Anaerobic
  const anScore =
    p.bod5 > 1000 || p.cod > 2000 ? 95 :
    p.bod5 > 500  || p.cod > 1000 ? 80 :
    p.bod5 > 300  || p.cod > 600  ? 60 : 15;
  eq.push({
    id: 'anaerobic', name: 'Anaerobic Treatment (UASB)', path: '/aeration-spec',
    score: anScore,
    priority: anScore >= 75 ? 'recommended' : anScore >= 50 ? 'optional' : 'not_applicable',
    stage: 'secondary',
    triggerParams: [
      { param: 'BOD₅', value: p.bod5, threshold: 500, unit: 'mg/L' },
      { param: 'COD',  value: p.cod,  threshold: 1000, unit: 'mg/L' },
    ],
    reason: anScore >= 75
      ? `High strength waste (BOD ${p.bod5} / COD ${p.cod} mg/L) — anaerobic pre-treatment is energy positive.`
      : `Organic load below anaerobic threshold — aerobic treatment preferred.`,
    designNote: 'OLR: 5–15 kg COD/m³·d. HRT: 4–12 h (UASB). Biogas capture: ~0.35 m³ CH₄/kg COD removed.',
  });

  // GAC
  const gacScore =
    (p.cod > 200 && ratio < 0.3) ? 95 :
    (p.cod > 100 && ratio < 0.4) ? 70 :
    p.phenol > 10                 ? 85 :
    p.color > 100                 ? 70 : 20;
  eq.push({
    id: 'gac', name: 'GAC Filter', path: '/gac-spec',
    score: gacScore,
    priority: gacScore >= 75 ? 'recommended' : gacScore >= 50 ? 'optional' : 'not_applicable',
    stage: 'tertiary',
    triggerParams: [
      { param: 'COD',      value: p.cod,    threshold: 100, unit: 'mg/L' },
      { param: 'BOD:COD',  value: ratio,    threshold: 0.4, unit: '' },
      { param: 'Colour',   value: p.color,  threshold: 50,  unit: 'Pt-Co' },
    ],
    reason: gacScore >= 75
      ? `Refractory COD (BOD:COD ${ratio.toFixed(2)}) — GAC adsorption required for final polishing.`
      : `GAC optional for colour removal or trace contaminant polishing.`,
    designNote: `EBCT: 10–20 min. SLR: 5–15 m³/m²·h. Iodine No. >850 mg/g. Reactivation at breakthrough.`,
  });

  // Multimedia Filter
  const mmfScore =
    p.tss > 30 && p.tss < 150 ? 80 :
    p.turbidity > 10            ? 75 : 35;
  eq.push({
    id: 'multimedia', name: 'Multimedia Filter', path: '/multimedia-spec',
    score: mmfScore,
    priority: mmfScore >= 70 ? 'recommended' : 'optional',
    stage: 'polishing',
    triggerParams: [
      { param: 'TSS',       value: p.tss,       threshold: 30,  unit: 'mg/L' },
      { param: 'Turbidity', value: p.turbidity,  threshold: 10,  unit: 'NTU' },
    ],
    reason: 'Final polishing for TSS, turbidity and BOD. Protects downstream membranes or UV systems.',
    designNote: 'Rate: 8–15 m³/m²·h. 3-layer media: anthracite/sand/garnet. Backwash: 15–25 m³/m²·h.',
  });

  // NH3 Stripper
  const nh3Score =
    p.ammoniaN > 100 ? 95 :
    p.ammoniaN > 50  ? 75 : 10;
  if (nh3Score > 20) {
    eq.push({
      id: 'ammonia-stripper', name: 'Ammonia Stripper', path: '/ammonia-stripper-spec',
      score: nh3Score,
      priority: nh3Score >= 75 ? 'recommended' : 'optional',
      stage: 'primary',
      triggerParams: [{ param: 'NH₃-N', value: p.ammoniaN, threshold: 50, unit: 'mg/L' }],
      reason: `NH₃-N ${p.ammoniaN} mg/L — air stripping effective for high-strength ammonia removal before biological treatment.`,
      designNote: 'Requires pH ≥10.0 (lime dosing). Air:water ratio 100–400. Effective above 15°C.',
    });
  }

  // Buffer Tank
  eq.push({
    id: 'buffer', name: 'Buffer Tank', path: '/buffer-spec',
    score: 70, priority: 'recommended', stage: 'tertiary',
    triggerParams: [],
    reason: 'Surge protection between treatment stages; ensures stable discharge flow and pump control.',
    designNote: 'Detention: 0.5–4 h. Working volume: 50–85%. Level control with VFD pumps.',
  });

  // Sludge Holding — only when there's actually sludge to handle.
  // Borehole / boiler-feed waters with TSS < 5 mg/L and no biological/chemical
  // precipitation step generate essentially zero sludge; recommending sludge
  // handling there is misleading. Trigger only if:
  //   • TSS ≥ 20 mg/L (real particulate load to capture in primary/sec), OR
  //   • COD ≥ 200 mg/L AND BOD/COD ≥ 0.3 (biological process → biomass), OR
  //   • Heavy metals present (chemical precipitation sludge).
  const sludgeRatio = p.cod > 0 ? p.bod5 / p.cod : 0;
  const heavyMetals = (p.lead ?? 0) + (p.cadmium ?? 0) + (p.chromium ?? 0) + (p.nickel ?? 0) + (p.copper ?? 0) + (p.zinc ?? 0);
  const generatesSludge = p.tss >= 20 || (p.cod >= 200 && sludgeRatio >= 0.3) || heavyMetals > 1;
  if (generatesSludge) {
    eq.push({
      id: 'sludge', name: 'Sludge Holding', path: '/sludge-spec',
      score: 90, priority: 'essential', stage: 'tertiary',
      triggerParams: ['tss', 'cod', 'metals'],
      reason: 'Physical/chemical/biological treatment generates sludge that requires thickening and storage.',
      designNote: `Est. sludge production: ${Math.round(p.tss * p.flowRate * 1.5 / 1000)} kg DS/d. Storage: 2–7 days.`,
    });
  }

  return eq.sort((a, b) => b.score - a.score);
}

// ─── Recommended Treatment Train ───────────────────────────────────────────

export function buildRecommendedTrain(p: WaterParameters): TreatmentTrainStep[] {
  const ratio = p.cod > 0 ? p.bod5 / p.cod : 0;
  const train: TreatmentTrainStep[] = [];
  let order = 1;

  // Pre-treatment: always include EQ
  train.push({
    order: order++, equipment: 'Equalization Tank', equipmentId: 'eq', stage: 'pre-treatment',
    purpose: 'Flow and load equalisation, peak shaving, mixing',
    keyRemoval: [{ param: 'tss', label: 'TSS', expected: 10 }],
    designParam: 'HRT: 12 h | Working vol: 80%',
  });

  // pH adjustment if needed
  if (p.ph < 6.0 || p.ph > 9.5) {
    train.push({
      order: order++, equipment: 'pH Adjustment', equipmentId: 'ph', stage: 'pre-treatment',
      purpose: `pH correction from ${p.ph.toFixed(1)} to 7.0–8.0`,
      keyRemoval: [],
      chemDosing: p.ph < 7
        ? [{ chemical: 'NaOH (32% solution)', dose: '50–200 mg/L', purpose: 'pH elevation' }]
        : [{ chemical: 'H₂SO₄ (93%)', dose: '20–100 mg/L', purpose: 'pH reduction' }],
      designParam: 'Retention: 20–45 min | 2-stage dosing',
    });
  }

  // NH3 stripping if very high ammonia
  if (p.ammoniaN > 100) {
    train.push({
      order: order++, equipment: 'Ammonia Stripper', equipmentId: 'ammonia-stripper', stage: 'primary',
      purpose: 'High-strength ammonia removal before biological treatment',
      keyRemoval: [{ param: 'ammoniaN', label: 'NH₃-N', expected: 90 }],
      chemDosing: [{ chemical: 'Ca(OH)₂ (lime)', dose: '200–800 mg/L', purpose: 'pH elevation to ≥10' }],
      designParam: 'pH ≥10 | Air:Water 200–400 | Temp ≥15°C',
    });
  }

  // DAF if high O&G or TSS
  if (p.oilGrease > 25 || p.tss > 100) {
    train.push({
      order: order++, equipment: 'DAF Unit', equipmentId: 'daf', stage: 'primary',
      purpose: 'Dissolved air flotation — FOG and suspended solids removal',
      keyRemoval: [
        { param: 'oilGrease', label: 'O&G', expected: 90 },
        { param: 'tss', label: 'TSS', expected: 80 },
        { param: 'bod5', label: 'BOD₅', expected: 35 },
      ],
      chemDosing: [
        { chemical: 'FeCl₃ or Alum', dose: '10–50 mg/L', purpose: 'Coagulation' },
        { chemical: 'Anionic polymer', dose: '2–5 mg/L', purpose: 'Flocculation' },
      ],
      designParam: 'SLR: 3–6 m³/m²·h | Recycle: 15–25% | 5 bar saturator',
    });
  } else if (p.tss > 50) {
    train.push({
      order: order++, equipment: 'Lamella Clarifier', equipmentId: 'lamella', stage: 'primary',
      purpose: 'Inclined plate settling for suspended solids removal',
      keyRemoval: [
        { param: 'tss', label: 'TSS', expected: 75 },
        { param: 'bod5', label: 'BOD₅', expected: 25 },
      ],
      chemDosing: [
        { chemical: 'Alum / FeCl₃', dose: '20–40 mg/L', purpose: 'Coagulation' },
        { chemical: 'Polymer', dose: '1–3 mg/L', purpose: 'Flocculation' },
      ],
      designParam: 'SOR: 0.5–1.5 m³/m²·h | Plate angle: 55° | Sludge withdrawal: continuous',
    });
  }

  // Anaerobic if very high strength
  if (p.bod5 > 600 || p.cod > 1500) {
    train.push({
      order: order++, equipment: 'Anaerobic Treatment (UASB)', equipmentId: 'anaerobic', stage: 'secondary',
      purpose: 'High-strength BOD/COD reduction with biogas recovery',
      keyRemoval: [
        { param: 'bod5', label: 'BOD₅', expected: 70 },
        { param: 'cod', label: 'COD', expected: 65 },
      ],
      designParam: `OLR: 5–15 kg COD/m³·d | HRT: 6–12 h | Biogas: ~${Math.round(p.cod * p.flowRate * 0.00035)} m³/d`,
    });
  }

  // Aerobic biological
  if (p.bod5 > 30 && ratio > 0.2) {
    train.push({
      order: order++, equipment: 'Aeration / MBBR', equipmentId: 'aeration', stage: 'secondary',
      purpose: `Aerobic biological treatment — BOD/COD/NH₃-N removal. ${ratio > 0.4 ? 'Excellent substrate.' : 'Moderate biodegradability.'}`,
      keyRemoval: [
        { param: 'bod5', label: 'BOD₅', expected: 93 },
        { param: 'cod', label: 'COD', expected: 85 },
        { param: 'ammoniaN', label: 'NH₃-N', expected: 90 },
        { param: 'tss', label: 'TSS', expected: 85 },
      ],
      chemDosing: p.alkalinity < 150 ? [{ chemical: 'NaHCO₃', dose: '100–300 mg/L', purpose: 'Alkalinity supplementation for nitrification' }] : undefined,
      designParam: `F/M: 0.15–0.35 | SRT: 12–18 d | DO: 2–3 mg/L | HRT: ${p.bod5 > 500 ? '18–24' : '8–16'} h`,
    });

    // Secondary clarifier
    train.push({
      order: order++, equipment: 'Secondary Clarifier', equipmentId: 'secondary', stage: 'secondary',
      purpose: 'Biomass separation and sludge return to biological reactor',
      keyRemoval: [
        { param: 'tss', label: 'TSS', expected: 90 },
        { param: 'bod5', label: 'BOD₅', expected: 20 },
      ],
      designParam: 'SOR: 0.6–1.0 m³/m²·h | SLR: 3–5 kg/m²·h | RAS: 50–100%',
    });
  }

  // P removal if needed
  if (p.totalPhosphorus > 3) {
    train[train.length - 1].chemDosing = [
      ...(train[train.length - 1].chemDosing ?? []),
      { chemical: 'FeCl₃ (as-needed)', dose: '15–50 mg/L', purpose: `Phosphorus precipitation from ${p.totalPhosphorus.toFixed(1)} mg/L` },
    ];
  }

  // Polishing: Multimedia filter
  if (p.tss > 20 || p.turbidity > 10) {
    train.push({
      order: order++, equipment: 'Multimedia Filter', equipmentId: 'multimedia', stage: 'polishing',
      purpose: 'Final TSS and turbidity polishing before discharge or reuse',
      keyRemoval: [
        { param: 'tss', label: 'TSS', expected: 80 },
        { param: 'turbidity', label: 'Turbidity', expected: 85 },
      ],
      designParam: 'Rate: 10–12 m³/m²·h | BW: 18–20 m³/m²·h | 3-layer media',
    });
  }

  // GAC if refractory COD
  if (ratio < 0.35 && p.cod > 80) {
    train.push({
      order: order++, equipment: 'GAC Filter', equipmentId: 'gac', stage: 'polishing',
      purpose: 'Adsorption of refractory organics, colour and trace contaminants',
      keyRemoval: [
        { param: 'cod', label: 'COD', expected: 60 },
        { param: 'color', label: 'Colour', expected: 80 },
      ],
      designParam: 'EBCT: 15 min | SLR: 8 m³/m²·h | Carbon: 800 kg/m³ bed',
    });
  }

  // Sludge holding — only when there is sludge to hold.
  // Same gate as buildEquipmentList: TSS ≥ 20 mg/L, biological load, or
  // heavy metals (precipitation sludge). Skip entirely for clean borehole /
  // boiler-feed water.
  const heavyMetals = (p.lead ?? 0) + (p.cadmium ?? 0) + (p.chromium ?? 0) + (p.nickel ?? 0) + (p.copper ?? 0) + (p.zinc ?? 0);
  const generatesSludge = p.tss >= 20 || (p.cod >= 200 && ratio >= 0.3) || heavyMetals > 1;
  if (generatesSludge) {
    train.push({
      order: order++, equipment: 'Sludge Holding', equipmentId: 'sludge', stage: 'polishing',
      purpose: 'Sludge thickening, storage and dewatering preparation',
      keyRemoval: [],
      chemDosing: [{ chemical: 'Anionic polymer', dose: '4–8 kg/t DS', purpose: 'Dewatering aid' }],
      designParam: `Storage: 3–5 days | Thickening: 2–4× | DS target: 3–6%`,
    });
  }

  return train;
}

// ─── Overall Treatability Score ────────────────────────────────────────────

export function calcTreatabilityScore(p: WaterParameters): { score: number; rating: TreatabilityReport['treatabilityRating']; color: string } {
  let score = 100;
  const ratio = p.cod > 0 ? p.bod5 / p.cod : 0;

  // Biodegradability penalty
  if (ratio < 0.1)       score -= 35;
  else if (ratio < 0.2)  score -= 25;
  else if (ratio < 0.35) score -= 15;
  else if (ratio < 0.5)  score -= 5;

  // Toxicity penalties
  const toxics = screenToxicity(p);
  for (const t of toxics) {
    if (t.riskLevel === 'critical') score -= 20;
    else if (t.riskLevel === 'high') score -= 12;
    else if (t.riskLevel === 'moderate') score -= 6;
    else if (t.riskLevel === 'low') score -= 2;
  }

  // pH penalty
  if (p.ph < 4 || p.ph > 12)        score -= 15;
  else if (p.ph < 5.5 || p.ph > 11) score -= 8;
  else if (p.ph < 6 || p.ph > 10)   score -= 3;

  // Alkalinity penalty
  if (p.ammoniaN > 0 && p.alkalinity < p.ammoniaN * 7.14) score -= 8;

  score = Math.max(5, Math.min(100, score));

  const rating: TreatabilityReport['treatabilityRating'] =
    score >= 80 ? 'excellent' :
    score >= 65 ? 'good'      :
    score >= 50 ? 'moderate'  :
    score >= 30 ? 'challenging': 'complex';

  const color =
    score >= 80 ? '#10b981' :
    score >= 65 ? '#84cc16' :
    score >= 50 ? '#f59e0b' :
    score >= 30 ? '#f97316' : '#ef4444';

  return { score, rating, color };
}

// ─── Master Analysis Function ──────────────────────────────────────────────

export function runTreatabilityAnalysis(
  p: WaterParameters,
  sampleName: string,
  sampleDate: string
): TreatabilityReport {
  const industryOrigin = inferIndustryOrigin(p);
  const biodegradability = assessBiodegradability(p);
  const nutrientBalance = assessNutrientBalance(p);
  const toxicityScreen = screenToxicity(p);
  const buffering = assessBuffering(p);
  const settleability = assessSettleability(p);
  const { score: overallTreatabilityScore, rating: treatabilityRating, color: treatabilityColor } = calcTreatabilityScore(p);
  const equipmentSuitability = scoreEquipmentSuitability(p);
  const recommendedTrain = buildRecommendedTrain(p);

  // Key findings
  const keyFindings: string[] = [];
  keyFindings.push(`Origin classified as ${industryOrigin.primary} (${industryOrigin.confidence} confidence, ${industryOrigin.confidencePct}%)`);
  keyFindings.push(`BOD:COD ratio ${biodegradability.bodCodRatio.toFixed(2)} — ${biodegradability.label}`);
  keyFindings.push(`Biological treatment suitability: ${biodegradability.biologicalTreatmentSuitability}%`);

  if (nutrientBalance.nStatus !== 'adequate') keyFindings.push(`Nitrogen ${nutrientBalance.nStatus} (BOD:N = ${nutrientBalance.bodNRatio?.toFixed(1)})`);
  if (nutrientBalance.pStatus !== 'adequate') keyFindings.push(`Phosphorus ${nutrientBalance.pStatus} (BOD:P = ${nutrientBalance.bodPRatio?.toFixed(1)})`);
  if (toxicityScreen.length > 0) keyFindings.push(`${toxicityScreen.length} inhibitory compound(s) detected — pre-treatment required`);
  if (!buffering.adequateBuffering) keyFindings.push(`Insufficient alkalinity for nitrification — supplement ${Math.round(buffering.recommendedMinAlkalinity - buffering.alkalinity)} mg/L`);

  // Warnings
  const warnings: string[] = [];
  if (toxicityScreen.some(t => t.riskLevel === 'critical')) warnings.push('CRITICAL: Biological treatment will be severely inhibited without pre-treatment');
  if (toxicityScreen.some(t => t.riskLevel === 'high'))     warnings.push('High toxicity detected — conduct toxicity bioassay before commissioning biological plant');
  if (biodegradability.classification === 'recalcitrant')   warnings.push('Recalcitrant waste stream — conventional biological treatment will not meet discharge limits');
  if (p.ph < 5)  warnings.push(`Extremely acidic pH ${p.ph.toFixed(1)} — corrosion risk to equipment and infrastructure`);
  if (p.ph > 11) warnings.push(`Extremely alkaline pH ${p.ph.toFixed(1)} — hazardous to handle; specialist materials required`);
  if (p.oilGrease > 200) warnings.push(`Very high O&G ${p.oilGrease} mg/L — pre-treatment separation essential before any downstream equipment`);

  return {
    sampleName, sampleDate, industryOrigin, biodegradability,
    nutrientBalance, toxicityScreen, buffering, settleability,
    overallTreatabilityScore, treatabilityRating, treatabilityColor,
    equipmentSuitability, recommendedTrain, keyFindings, warnings,
  };
}

// ─── Effluent Compliance Functions ────────────────────────────────────────

const PARAM_LABELS_COMPLIANCE: Record<string, { label: string; unit: string; limits?: Record<string, number> }> = {
  bod5:            { label: 'BOD₅',         unit: 'mg/L' },
  cod:             { label: 'COD',           unit: 'mg/L' },
  tss:             { label: 'TSS',           unit: 'mg/L' },
  ph:              { label: 'pH',            unit: '' },
  ammoniaN:        { label: 'NH₃-N',         unit: 'mg/L' },
  totalNitrogen:   { label: 'Total N',       unit: 'mg/L' },
  totalPhosphorus: { label: 'Total P',       unit: 'mg/L' },
  oilGrease:       { label: 'Oil & Grease',  unit: 'mg/L' },
  tds:             { label: 'TDS',           unit: 'mg/L' },
  turbidity:       { label: 'Turbidity',     unit: 'NTU' },
  conductivity:    { label: 'Conductivity',  unit: 'µS/cm' },
  temperature:     { label: 'Temperature',   unit: '°C' },
  lead:            { label: 'Lead (Pb)',     unit: 'mg/L' },
  chromium:        { label: 'Chromium (Cr)', unit: 'mg/L' },
  cadmium:         { label: 'Cadmium (Cd)', unit: 'mg/L' },
  nickel:          { label: 'Nickel (Ni)',   unit: 'mg/L' },
  zinc:            { label: 'Zinc (Zn)',     unit: 'mg/L' },
  copper:          { label: 'Copper (Cu)',   unit: 'mg/L' },
  phenol:          { label: 'Phenol',        unit: 'mg/L' },
  sulfides:        { label: 'Sulfides',      unit: 'mg/L' },
  surfactants:     { label: 'Surfactants',   unit: 'mg/L' },
};

export const PARAM_META = PARAM_LABELS_COMPLIANCE;

export function assessCompliance(
  params: Record<string, number>,
  limits: Record<string, number>
): ComplianceParameter[] {
  return Object.entries(PARAM_LABELS_COMPLIANCE)
    .map(([param, meta]) => {
      const value = params[param] ?? 0;
      const limit = limits[param] ?? 0;
      if (value <= 0 || limit <= 0) return null;

      const ratio = value / limit;
      const margin = ((limit - value) / limit) * 100;
      const pass = value <= limit;

      const riskLevel: ComplianceParameter['riskLevel'] =
        !pass && ratio > 2   ? 'critical' :
        !pass                ? 'exceeded' :
        ratio > 0.8          ? 'near_limit' : 'compliant';

      const color =
        riskLevel === 'critical'   ? '#ef4444' :
        riskLevel === 'exceeded'   ? '#f97316' :
        riskLevel === 'near_limit' ? '#f59e0b' : '#10b981';

      return { param, label: meta.label, unit: meta.unit, value, limit, ratio, pass, margin, riskLevel, color } satisfies ComplianceParameter;
    })
    .filter((x): x is ComplianceParameter => x !== null)
    .sort((a, b) => b.ratio - a.ratio);
}

export function calcRemovalEfficiencies(
  influentParams: Record<string, number>,
  effluentParams: Record<string, number>
): RemovalEfficiency[] {
  const expectedRanges: Record<string, [number, number, string]> = {
    bod5:            [85, 98, 'BOD₅'],
    cod:             [75, 95, 'COD'],
    tss:             [80, 98, 'TSS'],
    oilGrease:       [80, 98, 'O&G'],
    ammoniaN:        [80, 99, 'NH₃-N'],
    totalNitrogen:   [50, 85, 'Total N'],
    totalPhosphorus: [60, 95, 'Total P'],
    turbidity:       [70, 98, 'Turbidity'],
  };

  return Object.entries(expectedRanges)
    .map(([param, [minExp, maxExp, label]]) => {
      const inf = influentParams[param] ?? 0;
      const eff = effluentParams[param] ?? 0;
      if (inf <= 0) return null;

      const removalPct = Math.max(0, ((inf - eff) / inf) * 100);
      const performance: RemovalEfficiency['performance'] =
        removalPct >= maxExp        ? 'excellent' :
        removalPct >= minExp        ? 'good'      :
        removalPct >= minExp * 0.8  ? 'adequate'  :
        removalPct >= minExp * 0.5  ? 'poor'      : 'failing';

      const unit = PARAM_LABELS_COMPLIANCE[param]?.unit ?? 'mg/L';
      return { param, label, unit, influentValue: inf, effluentValue: eff, removalPct, expectedMin: minExp, expectedMax: maxExp, performance } satisfies RemovalEfficiency;
    })
    .filter((x): x is RemovalEfficiency => x !== null);
}

export function assessRegulatoryRisk(compliance: ComplianceParameter[]): RegulatoryRisk {
  const exceeded = compliance.filter(c => !c.pass);
  const critical = compliance.filter(c => c.riskLevel === 'critical');
  const nearLimit = compliance.filter(c => c.riskLevel === 'near_limit');

  let score = 0;
  for (const c of compliance) {
    if (c.riskLevel === 'critical')   score += 25;
    else if (c.riskLevel === 'exceeded') score += 15;
    else if (c.riskLevel === 'near_limit') score += 5;
  }
  score = Math.min(100, score);

  const level: RegulatoryRisk['level'] =
    score >= 60 ? 'critical' :
    score >= 35 ? 'high'     :
    score >= 15 ? 'moderate' : 'low';

  const consequences: Record<RegulatoryRisk['level'], string[]> = {
    critical: ['Discharge consent breach — enforcement action likely', 'Environmental permit suspension risk', 'Regulatory fine or prosecution', 'Immediate cessation of discharge may be required'],
    high:     ['Reportable breach of discharge conditions', 'Regulatory notice possible', 'Compliance audit likely', 'Corrective action plan required'],
    moderate: ['Minor breach — improvement notice risk', 'Consent review recommended', 'Monitoring increase likely'],
    low:      ['Within consent — continuous monitoring required', 'Near-limit parameters require attention', 'Maintain current treatment performance'],
  };

  const immediateActions =
    level === 'critical' ? ['Stop or reduce discharge immediately', 'Notify regulatory authority', 'Emergency chemical dosing review', 'Engage environmental consultant'] :
    level === 'high'     ? ['Increase chemical dosing', 'Review process performance', 'Notify permit holder', 'Prepare incident report'] :
    level === 'moderate' ? ['Increase sampling frequency', 'Review dosing rates', 'Inspect equipment'] :
    ['Continue monitoring', 'Review maintenance schedule'];

  return {
    level, score,
    exceedingParams: exceeded.map(c => c.label),
    nearLimitParams: nearLimit.map(c => c.label),
    consequenceNotes: consequences[level],
    immediateActions,
  };
}
